import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Button, Card, Input, Spinner } from '@/components/admin/ui';
import {
  BarList,
} from '@/components';
import { apiJson, ApiError } from '@/lib/api';
import { formatAdminDateTime, formatInt } from '@/lib/format';

type AppVersionConfigItem = {
  platform: string;
  latest_version: string | null;
  soft_wall_version: string | null;
  hard_wall_version: string | null;
  updated_at: string;
};

type VersionCount = { key: string; count: number };

type PlatformWindowAdoption = {
  total_devices: number;
  versions: VersionCount[];
};

type PlatformAdoption = {
  last_24h: PlatformWindowAdoption;
  last_72h: PlatformWindowAdoption;
};

type AppVersionAdoptionResponse = {
  ios: PlatformAdoption;
  android: PlatformAdoption;
};

type PlatformFormState = {
  latest_version: string;
  soft_wall_version: string;
  hard_wall_version: string;
};

const PLATFORM_LABELS: Record<string, string> = {
  ios: 'iOS',
  android: 'Android',
};

function emptyForm(): PlatformFormState {
  return {
    latest_version: '',
    soft_wall_version: '',
    hard_wall_version: '',
  };
}

function formFromItem(item: AppVersionConfigItem | undefined): PlatformFormState {
  if (!item) return emptyForm();
  return {
    latest_version: item.latest_version ?? '',
    soft_wall_version: item.soft_wall_version ?? '',
    hard_wall_version: item.hard_wall_version ?? '',
  };
}

function parseDetail(err: unknown): string {
  if (err instanceof ApiError && err.bodyText) {
    try {
      const j = JSON.parse(err.bodyText) as { detail?: string | string[] };
      if (typeof j.detail === 'string') return j.detail;
      if (Array.isArray(j.detail)) return j.detail.map(String).join('; ');
    } catch {
      /* ignore */
    }
    return err.bodyText;
  }
  return err instanceof Error ? err.message : 'Request failed';
}

function nullableField(value: string): string | null {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

export const Route = createFileRoute('/admin/app-version')({
  component: AppVersionPage,
});

function AppVersionPage() {
  const qc = useQueryClient();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin', 'app-version'],
    queryFn: () => apiJson<AppVersionConfigItem[]>('/v1/admin/app-version'),
  });

  const adoptionQ = useQuery({
    queryKey: ['admin', 'app-version', 'adoption'],
    queryFn: () => apiJson<AppVersionAdoptionResponse>('/v1/admin/app-version/adoption'),
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <p className="text-sm text-red-600">
        {(error as Error).message}
        <button type="button" className="ml-2 underline" onClick={() => void refetch()}>
          Retry
        </button>
      </p>
    );
  }

  return (
    <div className="space-y-6 pb-10">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">App version</h1>
        <p className="mt-1 text-sm text-nova-muted-foreground">
          Per-platform thresholds returned by{' '}
          <code className="text-xs">GET /v1/app-version</code>. Leave a field blank to
          disable that tier. Dominant versions below each card use devices seen in the
          last 24h / 72h.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {(['ios', 'android'] as const).map((platform) => (
          <div key={platform} className="space-y-4">
            <PlatformConfigCard
              platform={platform}
              item={data?.find((row) => row.platform === platform)}
              onSaved={() => {
                void qc.invalidateQueries({ queryKey: ['admin', 'app-version'] });
              }}
            />
            <PlatformAdoptionCard
              platform={platform}
              adoption={adoptionQ.data?.[platform]}
              loading={adoptionQ.isLoading}
              error={adoptionQ.error ? (adoptionQ.error as Error).message : null}
              onRetry={() => void adoptionQ.refetch()}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function PlatformAdoptionCard({
  platform,
  adoption,
  loading,
  error,
  onRetry,
}: {
  platform: 'ios' | 'android';
  adoption: PlatformAdoption | undefined;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
}) {
  return (
    <Card className="min-w-0">
      <h3 className="text-sm font-semibold text-nova-foreground">
        Dominant versions · {PLATFORM_LABELS[platform]}
      </h3>
      <p className="mt-1 text-xs text-nova-muted-foreground">
        Devices with activity in each rolling window, by last registered app version.
      </p>

      {error ? (
        <p className="mt-3 text-sm text-red-600">
          {error}
          <button type="button" className="ml-2 underline" onClick={onRetry}>
            Retry
          </button>
        </p>
      ) : loading && !adoption ? (
        <div className="mt-4 h-28 animate-pulse rounded-lg bg-nova-muted" />
      ) : (
        <div className="mt-4 grid gap-5 sm:grid-cols-2">
          <AdoptionWindow
            label="Last 24 hours"
            window={adoption?.last_24h}
          />
          <AdoptionWindow
            label="Last 72 hours"
            window={adoption?.last_72h}
          />
        </div>
      )}
    </Card>
  );
}

function AdoptionWindow({
  label,
  window,
}: {
  label: string;
  window: PlatformWindowAdoption | undefined;
}) {
  const total = window?.total_devices ?? 0;
  return (
    <div>
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-nova-muted-foreground">
          {label}
        </h4>
        <span className="text-xs text-nova-muted-foreground">
          {formatInt(total)} device{total === 1 ? '' : 's'}
        </span>
      </div>
      <BarList
        data={window?.versions ?? []}
        total={total}
        emptyLabel="No devices in this window"
      />
    </div>
  );
}

function PlatformConfigCard({
  platform,
  item,
  onSaved,
}: {
  platform: 'ios' | 'android';
  item: AppVersionConfigItem | undefined;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<PlatformFormState>(() => formFromItem(item));
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    setForm(formFromItem(item));
  }, [item]);

  const saveMutation = useMutation({
    mutationFn: () =>
      apiJson<AppVersionConfigItem>(`/v1/admin/app-version/${platform}`, {
        method: 'PUT',
        body: JSON.stringify({
          latest_version: nullableField(form.latest_version),
          soft_wall_version: nullableField(form.soft_wall_version),
          hard_wall_version: nullableField(form.hard_wall_version),
        }),
      }),
    onSuccess: () => {
      setSaveError(null);
      onSaved();
    },
    onError: (err) => {
      setSaveError(parseDetail(err));
    },
  });

  return (
    <Card className="min-w-0">
      <h2 className="mb-1 text-lg font-medium">{PLATFORM_LABELS[platform]}</h2>
      <p className="mb-4 text-sm text-nova-muted-foreground">
        Hard wall blocks the app, soft wall nudges an update, latest marks the current store
        version.
      </p>

      <div className="space-y-4">
        <Input
          label="Latest version"
          placeholder="e.g. 4.2.0"
          value={form.latest_version}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, latest_version: e.target.value }))
          }
        />
        <Input
          label="Soft wall version"
          placeholder="e.g. 4.0.0"
          value={form.soft_wall_version}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, soft_wall_version: e.target.value }))
          }
        />
        <Input
          label="Hard wall version"
          placeholder="e.g. 3.5.0"
          value={form.hard_wall_version}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, hard_wall_version: e.target.value }))
          }
        />
      </div>

      {saveError ? <p className="mt-3 text-sm text-red-600">{saveError}</p> : null}

      <div className="mt-4 flex items-center gap-3">
        <Button
          onClick={() => void saveMutation.mutate()}
          disabled={saveMutation.isPending}
        >
          {saveMutation.isPending ? 'Saving…' : 'Save'}
        </Button>
        {item?.updated_at ? (
          <p className="text-xs text-nova-muted-foreground">
            Last updated {formatAdminDateTime(item.updated_at)}
          </p>
        ) : (
          <p className="text-xs text-nova-muted-foreground">Not saved yet</p>
        )}
      </div>
    </Card>
  );
}
